import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ElementRef } from '@angular/core';
import { IqEventBusTransformationService } from '@dat/iq-event-bus';
import { first, last, max, min, sortBy } from 'lodash';
import { GraphColors } from '../models';
import { DateTime } from 'luxon';

/**
 * The rate types that are used to help coordinate showing series.
 * These will be used to help components on the outside to update the graph
 * without having to know too much about the inner workings of the graph.
 */
export type RateSeriesTypes =
    | 'brokerCarrierSpot'
    | 'shipperCarrierContract'
    | 'contributionsContract'
    | 'contributionsSpot';

/**
 * The legend settings for the legend component. This will help
 * the settings for the legend component to know what to show and what not to show.
 */
export type LegendSettings = {
    /**
     * The title of the legend.
     */
    title: string;
    /**
     * The color of the icon for the series.
     */
    color: string;
    /**
     * The id of this legend (used for targetting)
     */
    id: string;

    /**
     * Determines if this legend has a tooltip
     */
    tooltip: string;

    /**
     * Determines if the current legend is checked.
     *
     * If checked, it will show the various series
     */
    checked: boolean;

    /**
     * Determines if the legend is shown.
     */
    show: boolean;

    /**
     * Determines if the legend is a toggle allowing the
     * user to show and hide the series
     */
    toggle: boolean;

    /**
     * The series ids that are associated with this legend.
     */
    seriesIds: string[];

    /**
     * Icon style for legend
     */
    iconType?: 'line' | 'circle';
};

//#region Historical Legend

/**
 * The historical series ids that are available for the graph.
 * THis is to make it easier for the legend to toggle them off and on
 */
export type HistoricalSeriesIds =
    | 'brokerCarrierSpotHistoricalLineSeries'
    | 'brokerCarrierSpotHistoricalCandleStickSeries'
    | 'shipperCarrierContractHistoricalLineSeries'
    | 'shipperCarrierContractHistoricalCandleStickSeries'
    | 'brokerCarrierSpotCurrentCandleStickSeries'
    | 'brokerCarrierSpotCurrentLineSeries'
    | 'shipperCarrierContractCurrentCandleStickSeries'
    | 'shipperCarrierContractCurrentLineSeries';

/**
 * The ids for the historical series that are available for the graph.
 */
export const bcsHistoricalLineSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotHistoricalLineSeries';
export const bcsHistoricalCandlestickSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotHistoricalCandleStickSeries';
export const bcsCurrentCandlestickSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotCurrentCandleStickSeries';
export const bcsCurrentLineSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotCurrentLineSeries';
export const sccHistoricalLineSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractHistoricalLineSeries';
export const sccHistoricalCandlestickSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractHistoricalCandleStickSeries';
export const sccCurrentCandlestickSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractCurrentCandleStickSeries';
export const sccCurrentLineSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractCurrentLineSeries';

/**
 * The historical series ids that are available for the graph.
 *
 * This is to help coordinate the forecast vs histoqrical series.
 */
export const historicalIdList: HistoricalSeriesIds[] = [
    bcsHistoricalLineSeriesId,
    bcsHistoricalCandlestickSeriesId,
    sccHistoricalLineSeriesId,
    sccHistoricalCandlestickSeriesId,
];

export const isHistoricalLineSeries = (id: string): boolean => {
    return historicalIdList.includes(id as HistoricalSeriesIds);
};

export const historicalLegendMap: Record<RateSeriesTypes, LegendSettings> = {
    brokerCarrierSpot: {
        title: 'Historical Spot',
        color: GraphColors.BrokerCarrierSpot,
        id: 'historicalBrokerCarrierSpot',
        tooltip: '',
        checked: false,
        toggle: false,
        seriesIds: [
            'brokerCarrierSpotHistoricalLineSeries',
            'brokerCarrierSpotHistoricalCandleStickSeries',
        ],
        show: false,
        iconType: 'line',
    },
    shipperCarrierContract: {
        title: 'Historical Contract',
        color: GraphColors.ShipperCarrierContract,
        id: 'historicalBrokerCarrierContract',
        tooltip: '',
        checked: false,
        seriesIds: [
            'shipperCarrierContractHistoricalLineSeries',
            'shipperCarrierContractHistoricalCandleStickSeries',
        ],
        show: false,
        toggle: false,
        iconType: 'line',
    },
    contributionsContract: {
        title: 'My Company Reports',
        color: GraphColors.ShipperCarrierContract,
        id: 'ShipperCarrierContractContributionsSeries',
        tooltip: '',
        checked: false,
        seriesIds: ['ShipperCarrierContractContributionsSeries'],
        show: false,
        toggle: false,
        iconType: 'circle',
    },
    contributionsSpot: {
        title: 'My Company Reports',
        color: GraphColors.BrokerCarrierSpot,
        id: 'BrokerCarrierSpotContributionsSeries',
        tooltip: '',
        checked: false,
        seriesIds: ['BrokerCarrierSpotContributionsSeries'],
        show: false,
        toggle: false,
        iconType: 'circle',
    },
};

//#endregion

//#region Forecast Legend

/**
 * The forecast series ids that are available for the graph.
 * THis is to make it easier for the legend to toggle them off and on
 */
export type ForecastSeriesIds =
    | 'brokerCarrierSpotForecastLineSeries'
    | 'brokerCarrierSpotForecastAreaSeries'
    | 'shipperCarrierContractForecastLineSeries'
    | 'shipperCarrierContractForecastAreaSeries';

/**
 * The ids for the forecast series that are available for the graph.
 */
export const bcsForecastLineSeriesId: ForecastSeriesIds =
    'brokerCarrierSpotForecastLineSeries';
export const bcsForecastAreaSeriesId: ForecastSeriesIds =
    'brokerCarrierSpotForecastAreaSeries';
export const sccForecastLineSeriesId: ForecastSeriesIds =
    'shipperCarrierContractForecastLineSeries';
export const sccForecastAreaSeriesId: ForecastSeriesIds =
    'shipperCarrierContractForecastAreaSeries';

export const forecastLegendMap: Record<RateSeriesTypes, LegendSettings> = {
    brokerCarrierSpot: {
        title: 'Forecast Spot',
        color: GraphColors.BrokerCarrierSpot,
        id: 'forecastBrokerCarrierSpot',
        tooltip: 'The shaded area represents Median Absolute Error (MAE).',
        checked: false,
        toggle: false,
        seriesIds: [
            'brokerCarrierSpotForecastLineSeries',
            'brokerCarrierSpotForecastAreaSeries',
        ],
        show: false,
    },
    shipperCarrierContract: {
        title: 'Forecast Contract',
        color: GraphColors.ShipperCarrierContract,
        id: 'forecastBrokerCarrierContract',
        tooltip: 'The shaded area represents Median Absolute Error (MAE).',
        checked: false,
        seriesIds: [
            'shipperCarrierContractForecastLineSeries',
            'shipperCarrierContractForecastAreaSeries',
        ],
        show: false,
        toggle: false,
    },
    contributionsContract: {
        title: '',
        color: '',
        id: '',
        tooltip: '',
        checked: false,
        seriesIds: [],
        show: false,
        toggle: false,
    },
    contributionsSpot: {
        title: '',
        color: '',
        id: '',
        tooltip: '',
        checked: false,
        seriesIds: [],
        show: false,
        toggle: false,
    },
};

//#endregion

export type RateSeriesData = {
    [key: string]: {
        date: number;
        high: number;
        key: string;
        low: number;
        average: number;
        timeEscalation: string;
    };
};

//#region Current Graph
export function setUpCurrentGraph(container: ElementRef): {
    chart: am5xy.XYChart;
    root: am5.Root;
    xAxis: am5xy.CategoryAxis<any>;
    yAxis: am5xy.ValueAxis<any>;
} {
    const root = am5.Root.new(container.nativeElement, {
        tooltipContainerBounds: {
            top: 300,
            bottom: 100,
            left: 50,
            right: 50,
        },
    });

    root.timezone = am5.Timezone.new('UTC');

    //#region Set Up Chart
    const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            // maxTooltipDistance: -1,
        }),
    );
    //#endregion

    //#region Set Up X Axes
    let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {}),
            width: 80,
            paddingTop: 5,
            categoryField: 'timeEscalation',
        }),
    );

    let xRenderer = xAxis.get('renderer');

    xRenderer.labels.template.setAll({
        rotation: 60,
        fontSize: 12,
        fill: am5.color('#636D79'),
        fontFamily: 'Sequel Sans',
        paddingTop: 15,
        location: 1,
    });

    xRenderer.grid.template.setAll({
        strokeWidth: 0,
        visible: false,
    });

    xAxis.set(
        'tooltip',
        am5.Tooltip.new(root, {
            themeTags: ['axis'],
            forceHidden: true,
        }),
    );

    //#endregion

    //#region Set Up Y Axes

    const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {}),
            height: 260,
            autoZoom: false,
            paddingRight: 6,
            marginLeft: -8, // Default margin is taking around 24px.
        }),
    );

    let yRenderer = yAxis.get('renderer');

    yRenderer.grid.template.setAll({
        strokeDasharray: [4],
    });

    yRenderer.labels.template.setAll({
        fontSize: 10,
        fontFamily: 'Sequel Sans',
        fill: am5.color('#636D79'),
    });
    //#endregion

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}
//#endregion

//#region Timeline Graph
export function setUpGraph(container: ElementRef): {
    chart: am5xy.XYChart;
    root: am5.Root;
    xAxis: am5xy.DateAxis<any>;
    yAxis: am5xy.ValueAxis<any>;
    contributionsXAxis: am5xy.DateAxis<any>;
} {
    const root = am5.Root.new(container.nativeElement, {
        tooltipContainerBounds: {
            top: 300,
            bottom: 100,
            left: 50,
            right: 50,
        },
    });
    root.timezone = am5.Timezone.new('UTC');

    //#region Set Up Chart
    const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            // maxTooltipDistance: -1,
        }),
    );
    //#endregion

    //#region Set Up X Axes
    let xAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {}),
            baseInterval: {
                timeUnit: 'day',
                count: 1,
            },
            gridIntervals: [
                { timeUnit: 'month', count: 1 },
                { timeUnit: 'day', count: 1 },
            ],
            periodChangeDateFormats: {
                day: 'MM/dd/yy',
                month: "MMM ''yy",
            },
            dateFormats: {
                day: 'MM/dd/yy',
                month: "MMM ''yy",
            },
            paddingTop: 6,
            markUnitChange: false,
        }),
    );

    let contributionsXAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {}),
            // min: this.mapDateTime(data?.default?.data?.[0]?.date, true),
            // max: this.mapDateTime(data?.default?.data?.[12]?.date, true),
            baseInterval: {
                timeUnit: 'day',
                count: 1,
            },
            visible: false,
            gridIntervals: [{ timeUnit: 'month', count: 1 }],
        }),
    );

    const xContributionsRenderer = contributionsXAxis.get('renderer');
    xContributionsRenderer.grid.template.setAll({
        height: 0,
        maxHeight: 0,
        strokeWidth: 0,
        strokeOpacity: 0,
    });

    let xRenderer = xAxis.get('renderer');

    xRenderer.labels.template.setAll({
        fill: am5.color('#636D79'),
        fontFamily: 'Sequel Sans',
        fontSize: 12,
        rotation: 60,
        dx: 10,
        // maxPosition: 0.99,
    });

    xRenderer.grid.template.setAll({
        strokeWidth: 0,
        visible: false,
    });

    xAxis.set(
        'tooltip',
        am5.Tooltip.new(root, {
            themeTags: ['axis'],
            forceHidden: true,
        }),
    );

    //#endregion

    //#region Set Up Y Axes

    const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {}),
            height: 260,
            autoZoom: false,
            paddingRight: 6,
            marginLeft: -8, // Default margin is taking around 24px.
        }),
    );

    let yRenderer = yAxis.get('renderer');

    yRenderer.grid.template.setAll({
        strokeDasharray: [4],
    });

    yRenderer.labels.template.setAll({
        fontSize: 12,
        fontFamily: 'Sequel Sans',
        fill: am5.color('#636D79'),
    });

    //#endregion

    return {
        chart,
        root,
        xAxis,
        yAxis,
        contributionsXAxis,
    };
}
//#endregion

//#region Forecast Graph Series
export function updateForecastGraph(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    },
    {
        brokerCarrierSpotForecast,
        shipperCarrierContractForecast,
        currentRate,
    }: {
        brokerCarrierSpotForecast: RateSeriesData;
        shipperCarrierContractForecast: RateSeriesData;
        currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract';
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
    { timeframe }: { timeframe: 'DAILY' | 'MONTHLY' },
) {
    //#region Series Set UP

    const brokerCarrierSpotForecastSeries: any[] =
        createBrokerCarrierSpotForecastSeries(
            brokerCarrierSpotForecast,
            {
                chart,
                root,
                xAxis,
                yAxis,
            },
            {
                perLoad,
            },
        );

    const shipperCarrierContractForecastSeries: any[] =
        createShipperCarrierContractForecastSeries(
            shipperCarrierContractForecast,
            {
                chart,
                root,
                xAxis,
                yAxis,
            },
            {
                perLoad,
            },
        );
    timeframe === 'DAILY' ? setXAxisToDaily(xAxis) : setXAxisToMonthly(xAxis);
    const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        snapToSeries: [
            currentRate === 'BrokerCarrierSpot'
                ? brokerCarrierSpotForecastSeries[0]
                : shipperCarrierContractForecastSeries[0],
        ],
        snapToSeriesBy: 'x',
        xAxis,
    });
    cursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', cursor);
    cursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636d79'),
        dy: 0,
    });

    //#region Y Axis Update

    /**
     * Clear the axis ranges and create new ones based on the new data.
     */
    yAxis.axisRanges.clear();

    /**
     * Get the min and max values from data to show on the graph
     */
    const { min, max } = getMinMaxYValues([
        ...Object.values(brokerCarrierSpotForecast),
        ...Object.values(shipperCarrierContractForecast),
    ]);

    yAxis.setAll({
        min: min,
        max: max,
        numberFormat: perLoad ? '$#' : '$#.00',
    });

    // yAxis.set('step', rangeStep);
    //#endregion

    chart.appear(1);

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}

//#endregion

//#region Forecast Tooltip

export type ForecastGraphInfo = {
    perLoad: boolean;
    currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract';
    brokerCarrierSpotForecast: RateSeriesData;
    shipperCarrierContractForecast: RateSeriesData;
};

export function addForecastTooltip(
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    legend: LegendSettings[],
    transformation: IqEventBusTransformationService,
    {
        brokerCarrierSpotForecast,
        shipperCarrierContractForecast,
        perLoad,
        currentRate,
    }: ForecastGraphInfo,
    monthly: boolean,
) {
    const isBrokerCarrierSpot = currentRate === 'BrokerCarrierSpot';
    const lineId = {
        BrokerCarrierSpot: bcsForecastLineSeriesId,
        ShipperCarrierContract: sccForecastLineSeriesId,
    }[currentRate];

    chartInfo.chart.series.each((series) => {
        const id = series.get('id');
        if (id !== lineId) return;
        const showBoth =
            currentRate === 'BrokerCarrierSpot'
                ? legend.find((l) => l.id === 'forecastBrokerCarrierContract')
                      ?.checked
                : legend.find((l) => l.id === 'forecastBrokerCarrierSpot')
                      ?.checked;
        series.dataItems.forEach((dataItem: { dataContext: any }) => {
            const spotInfo =
                brokerCarrierSpotForecast[dataItem.dataContext.key];
            const contractInfo =
                shipperCarrierContractForecast[dataItem.dataContext.key];

            const spotTemplate = transformation.render(createSpotTooltip(), {
                title: 'Spot Rate',
                date: DateTime.fromMillis(spotInfo.date, {
                    zone: 'UTC',
                }).toFormat(monthly ? 'MMMM yyyy' : 'MMMM dd, yyyy'),
                average: spotInfo?.average,
                low: spotInfo?.low,
                high: spotInfo?.high,
                withCents: !perLoad,
            });
            const contractTemplate = transformation.render(
                createContractTooltip(),
                {
                    title: 'Contract Rate',
                    date: DateTime.fromMillis(spotInfo.date, {
                        zone: 'UTC',
                    }).toFormat(monthly ? 'MMMM yyyy' : 'MMMM dd, yyyy'),
                    average: contractInfo?.average,
                    low: contractInfo?.low,
                    high: contractInfo?.high,
                    withCents: !perLoad,
                },
            );
            const bothTemplate = `<div class="bg-white grid grid-cols-2 gap-2 justify-center">
                ${isBrokerCarrierSpot ? spotTemplate : contractTemplate}
                ${isBrokerCarrierSpot ? contractTemplate : spotTemplate}
            </div>`;
            const template = showBoth
                ? bothTemplate
                : isBrokerCarrierSpot
                ? spotTemplate
                : contractTemplate;

            dataItem.dataContext = {
                ...dataItem.dataContext,
                template,
            };
        });

        const tooltip = series.set(
            'tooltip',
            am5.Tooltip.new(chartInfo.root, {
                animationDuration: 0,
                getFillFromSprite: false,
                getStrokeFromSprite: false,
            }),
        );

        chartInfo.chart.plotContainer.set('tooltipPosition', 'pointer');
        chartInfo.chart.plotContainer.set('tooltipHTML', '<div></div>');
        chartInfo.chart.plotContainer.set('tooltip', tooltip);

        const borderColor = showBoth
            ? '#000'
            : isBrokerCarrierSpot
            ? GraphColors.BrokerCarrierSpot
            : GraphColors.ShipperCarrierContract;

        tooltip.get('background').setAll({
            strokeWidth: 1,
            fill: am5.color('#fff'),
            stroke: am5.color(borderColor),
            strokeOpacity: 1,
            fillOpacity: 1,
        });

        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });

        tooltip.label.adapters.add('dy', (dy) => {
            tooltip.adapters.add('bounds', function () {
                return {
                    left: 70,
                    right: 9999,
                    top: 0,
                    bottom: 20,
                };
            });
            return -tooltip._fy;
        });

        tooltip.label.adapters.add('html', function (text, _target) {
            return `{template}`;
        });
    });
}
//#endregion

//#region Shipper Carrier Contract Forecast Series

function createShipperCarrierContractForecastSeries(
    shipperCarrierContractForecast,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): (am5xy.LineSeries | am5xy.CandlestickSeries)[] {
    const area: am5xy.LineSeries[] = createShipperCarrierContractForecastArea(
        root,
        xAxis,
        yAxis,
        shipperCarrierContractForecast,
    );

    const lineSeries: am5xy.LineSeries =
        createShipperCarrierContractForecastLineSeries(
            root,
            xAxis,
            yAxis,
            shipperCarrierContractForecast,
        );

    // chart.series.push(candleStick);
    chart.series.push(lineSeries);
    area.forEach((a) => chart.series.push(a));

    return [lineSeries];
}

function createShipperCarrierContractForecastLineSeries(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    ShipperCarrierContractForecast: any,
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccForecastLineSeriesId,
        name: 'forecastAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        valueXField: 'date',
        stroke: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    lineSeries.strokes.template.set('strokeDasharray', [3, 4]);

    lineSeries.data.setAll(Object.values(ShipperCarrierContractForecast));
    return lineSeries;
}

function createShipperCarrierContractForecastArea(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContractForecast: any,
): am5xy.LineSeries[] {
    const area: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccForecastAreaSeriesId,
        name: 'forecastHighLow',
        xAxis: xAxis,
        yAxis: yAxis,
        openValueYField: 'high',
        valueYField: 'low',
        valueXField: 'date',
        stroke: am5.color(GraphColors.ShipperCarrierContract),

        fill: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    area.fills.template.setAll({
        fillOpacity: 0.1,
        visible: true,
    });
    area.strokes.template.setAll({
        opacity: 0.1,
    });

    area.data.setAll(Object.values(shipperCarrierContractForecast));
    return [area];
}
//#endregion

//#region Broker Carrier Spot Forecast Series

function createBrokerCarrierSpotForecastSeries(
    BrokerCarrierSpotForecast,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): (am5xy.LineSeries | am5xy.CandlestickSeries)[] {
    const area: am5xy.LineSeries[] = createBrokerCarrierSpotForecastArea(
        root,
        xAxis,
        yAxis,
        BrokerCarrierSpotForecast,
    );

    const lineSeries: am5xy.LineSeries =
        createBrokerCarrierSpotForecastLineSeries(
            root,
            xAxis,
            yAxis,
            BrokerCarrierSpotForecast,
        );
    chart.series.push(lineSeries);
    area.forEach((a) => chart.series.push(a));

    return [lineSeries];
}

function createBrokerCarrierSpotForecastLineSeries(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    BrokerCarrierSpotForecast: any,
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsForecastLineSeriesId,
        name: 'forecastAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        valueXField: 'date',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    lineSeries.strokes.template.set('strokeDasharray', [3, 4]);

    lineSeries.data.setAll(Object.values(BrokerCarrierSpotForecast));
    return lineSeries;
}

function createBrokerCarrierSpotForecastArea(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    BrokerCarrierSpotForecast: any,
): am5xy.LineSeries[] {
    const area: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsForecastAreaSeriesId,
        name: 'forecastHighLow',
        xAxis: xAxis,
        yAxis: yAxis,
        openValueYField: 'high',
        valueYField: 'low',
        valueXField: 'date',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),

        fill: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    area.fills.template.setAll({
        fillOpacity: 0.1,
        visible: true,
    });
    area.strokes.template.setAll({
        opacity: 0.1,
    });

    area.data.setAll(Object.values(BrokerCarrierSpotForecast));
    return [area];
}

export type HistoricalGraphInfo = {
    perLoad: boolean;
    currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract';
    currentGraphChartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    };
    brokerCarrierSpotHistorical: RateSeriesData;
    shipperCarrierContractHistorical: RateSeriesData;
    brokerCarrierSpotCurrent: RateSeriesData;
    shipperCarrierContractCurrent: RateSeriesData;
    brokerCarrierSpotContributions: any[];
    shipperCarrierContractContributions: any[];
};

//#endregion

function createContributionsSeries(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    },
    rateType: string,
    brokerCarrierSpotContributions,
    shipperCarrierContractContributions,
    transformation: IqEventBusTransformationService,
): am5xy.LineSeries {
    const contributions =
        rateType === 'BrokerCarrierSpot'
            ? brokerCarrierSpotContributions
            : shipperCarrierContractContributions;

    const yField =
        rateType === 'BrokerCarrierSpot'
            ? 'totalPaidByBroker'
            : 'totalPaidByShipper';
    const id =
        historicalLegendMap[
            rateType === 'BrokerCarrierSpot'
                ? 'contributionsSpot'
                : 'contributionsContract'
        ].id;
    const scatterSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            id: id,
            name: 'ContributionsSeries',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'value',
            valueXField: 'date',
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
            }),
        }),
    );
    const color =
        rateType === 'BrokerCarrierSpot'
            ? GraphColors.BrokerCarrierSpot
            : GraphColors.ShipperCarrierContract;
    scatterSeries.set('stroke', am5.color(color));
    scatterSeries.set('fill', am5.color(color));
    scatterSeries.strokes.template.setAll({
        strokeWidth: 0,
        opacity: 0,
    });

    scatterSeries.bullets.push((root) => {
        return createBullet(root, scatterSeries);
    });

    if (!contributions?.length) return scatterSeries;

    scatterSeries.data.setAll(
        contributions.map((cont) => {
            const datetime = new Date(cont.pickupDate).getTime();
            const convertedDate = DateTime.fromMillis(datetime, {
                zone: 'UTC',
            });
            let name = '--';
            if (rateType === 'BrokerCarrierSpot') {
                if (cont.contributorType === 'Broker') {
                    name = cont.shipperName;
                }
                if (cont.contributorType === 'Carrier') {
                    name = cont.brokerName;
                }
            }
            if (rateType === 'ShipperCarrierContract') {
                if (cont.contributorType === 'Broker') {
                    name = cont.shipperName;
                }
                if (cont.contributorType === 'Carrier') {
                    name = cont.shipperName;
                }
            }

            let label = '--';
            if (rateType === 'BrokerCarrierSpot') {
                if (cont.contributorType === 'Broker') {
                    label = 'Shipper: ';
                }
                if (cont.contributorType === 'Carrier') {
                    label = 'Broker: ';
                }
            }
            if (rateType === 'ShipperCarrierContract') {
                if (cont.contributorType === 'Broker') {
                    label = 'Shipper: ';
                }
                if (cont.contributorType === 'Carrier') {
                    label = 'Shipper: ';
                }
            }

            let paidOrReceived = '';
            if (rateType === 'BrokerCarrierSpot') {
                if (cont.contributorType === 'Broker') {
                    paidOrReceived = 'Paid to carrier';
                }
                if (cont.contributorType === 'Carrier') {
                    paidOrReceived = 'Received';
                }
            }
            if (rateType === 'ShipperCarrierContract') {
                if (cont.contributorType === 'Broker') {
                    paidOrReceived = 'Received';
                }
                if (cont.contributorType === 'Carrier') {
                    paidOrReceived = 'Received';
                }
            }

            return {
                date: convertedDate.toMillis(),
                tooltipDate: convertedDate.toFormat('MMMM dd, yyyy'),
                value: cont?.[yField],
                userType: cont.contributorType,
                label,
                name,
                paidOrReceived,
                contractType: cont.rateType,
            };
        }),
    );
    return scatterSeries;
}

export function addContributionsTooltip(
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    legend: LegendSettings[],
    transformation: IqEventBusTransformationService,
    currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract',
    perLoad: boolean
) {
    chartInfo.chart.series.each((series) => {
        const id = series.get('id');
        const correctSeries =
            historicalLegendMap[
                currentRate === 'BrokerCarrierSpot'
                    ? 'contributionsSpot'
                    : 'contributionsContract'
            ].id;
        if (id !== correctSeries) return;
        series.dataItems.forEach((dataItem: { dataContext: any }) => {
            const contributionsTemplate = transformation.render(
                createTooltipContribution(),
                {
                    date: dataItem.dataContext.tooltipDate,
                    value: dataItem.dataContext.value,
                    userType: dataItem.dataContext.userType,
                    label: dataItem.dataContext.label,
                    name: dataItem.dataContext.name,
                    paidOrReceived: dataItem.dataContext.paidOrReceived,
                    contractType: dataItem.dataContext.contractType,
                    withCents: !perLoad,
                },
            );

            dataItem.dataContext = {
                ...dataItem.dataContext,
                template: contributionsTemplate,
            };
        });
        const tooltip = series.set(
            'tooltip',
            am5.Tooltip.new(chartInfo.root, {
                getFillFromSprite: false,
                getStrokeFromSprite: false,
                pointerOrientation: 'horizontal',
            }),
        );
        const borderColor =
            currentRate === 'BrokerCarrierSpot'
                ? GraphColors.BrokerCarrierSpot
                : GraphColors.ShipperCarrierContract;

        tooltip.get('background').setAll({
            strokeWidth: 1,
            fill: am5.color('#fff'),
            stroke: am5.color(borderColor),
            strokeOpacity: 1,
            fillOpacity: 1,
        });
        tooltip.label.adapters.add('html', function (text, _target) {
            return `{template}`;
        });
    });
}

function createTooltipContribution() {
    return `<div class="bg-white flex flex-col justify-center">
        <div class="text-center font-caption text-dat-grey-900 pb-1">
            <%= date %>
        </div>
        <div class="font-caption text-dat-grey-900">
        <%= label %> <strong><%= !!name ? name : '--' %></strong>
        </div>
        <div class="text-center font-caption text-dat-grey-900">
        <%= paidOrReceived %>: <strong><%= currency(value, withCents) %></strong>
        </div>
    </div>

`;
}

function createBullet(root: am5.Root, series: am5xy.LineSeries): am5.Bullet {
    const circle = am5.Circle.new(root, {
        radius: 4,
        stroke: series.get('stroke'),
        fill: am5.color('#fff'),
        fillOpacity: 0,
    });

    return am5.Bullet.new(root, {
        sprite: circle,
    });
}

//#region Historical Graph Series
export function updateHistoricalGraph(
    {
        chart,
        root,
        xAxis,
        yAxis,
        contributionsXAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
        contributionsXAxis: am5xy.DateAxis<any>;
    },
    {
        brokerCarrierSpotHistorical,
        shipperCarrierContractHistorical,
        brokerCarrierSpotCurrent,
        shipperCarrierContractCurrent,
        perLoad,
        currentGraphChartInfo,
        currentRate,
        brokerCarrierSpotContributions,
        shipperCarrierContractContributions,
    }: HistoricalGraphInfo,
    { timeframe }: { timeframe: 'DAILY' | 'MONTHLY' },
    transformation: IqEventBusTransformationService,
    showsContributions: boolean,
    rateType: string
) {
    //#region Series Set UP

    timeframe === 'DAILY' ? setXAxisToDaily(xAxis) : setXAxisToMonthly(xAxis);
    // xAxis.set('min', new Date('2023-10-01').getTime());
    // xAxis.set('max', new Date('2024-10-30').getTime());
    const hasContributions =
        !!brokerCarrierSpotContributions?.length ||
        !!shipperCarrierContractContributions?.length;

    const brokerCarrierSpotHistoricalSeries: [
        am5xy.LineSeries,
        am5xy.CandlestickSeries,
    ] = createBrokerCarrierSpotHistoricalSeries(
        brokerCarrierSpotHistorical,
        {
            chart,
            root,
            xAxis,
            yAxis,
        },
        {
            perLoad,
        },
    );

    const shipperCarrierContractHistoricalSeries: any[] =
        createShipperCarrierContractHistoricalSeries(
            shipperCarrierContractHistorical,
            {
                chart,
                root,
                xAxis,
                yAxis,
            },
            {
                perLoad,
            },
        );
    const currentData = {
        BrokerCarrierSpot: brokerCarrierSpotCurrent,
        ShipperCarrierContract: shipperCarrierContractCurrent,
    };

    const currentSeries: any[] = {
        BrokerCarrierSpot: createBrokerCarrierSpotCurrentSeries,
        ShipperCarrierContract: createShipperCarrierContractCurrentSeries,
    }[currentRate]?.(
        currentGraphChartInfo.root,
        currentGraphChartInfo.chart,
        currentGraphChartInfo.xAxis,
        currentGraphChartInfo.yAxis,
        currentData[currentRate],
    );

    const contributionsSeries: am5xy.LineSeries = createContributionsSeries(
        { chart, root, xAxis: contributionsXAxis, yAxis },
        currentRate,
        brokerCarrierSpotContributions,
        shipperCarrierContractContributions,
        transformation,
    );
    const xDates = Object.values(brokerCarrierSpotHistorical).map(
        (val) => val.date,
    );
    const xMin = Math.min(...xDates);
    const xMax = Math.max(...xDates);
    // xMax will be the first day of the month, so we want the max to be the end of that month
    const xMaxPlus1Month = DateTime.fromMillis(xMax)
        .plus({ months: 1 })
        .toMillis();
    contributionsXAxis.set('min', xMin);
    contributionsXAxis.set('max', xMaxPlus1Month);

    // setContributionsXAxisMinMax(contributionsXAxis, brokerCarrierSpotHistorical)

    const cursorSeries = hasContributions
        ? [
              contributionsSeries,
              currentRate === 'BrokerCarrierSpot'
                  ? brokerCarrierSpotHistoricalSeries[0]
                  : shipperCarrierContractHistoricalSeries[0],
          ]
        : [
              currentRate === 'BrokerCarrierSpot'
                  ? brokerCarrierSpotHistoricalSeries[0]
                  : shipperCarrierContractHistoricalSeries[0],
          ];
    // chart.series.clear();

    const historicalCursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        snapToSeries: cursorSeries,
        snapToSeriesBy: 'x',
        xAxis,
    });

    historicalCursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', historicalCursor);
    historicalCursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636d79'),
        dy: 0,
    });

    const currentCursor: am5xy.XYCursor = am5xy.XYCursor.new(
        currentGraphChartInfo.root,
        {
            behavior: 'none',
            snapToSeries: [currentSeries?.[0]],
            snapToSeriesBy: 'x',
            xAxis: currentGraphChartInfo.xAxis,
        },
    );
    currentCursor.lineY.setAll({
        visible: false,
    });
    currentGraphChartInfo.chart.set('cursor', currentCursor);
    currentCursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636d79'),
        dy: -100,
    });

    //#region Y Axis Update

    /**
     * Clear the axis ranges and create new ones based on the new data.
     */
    yAxis.axisRanges.clear();

    const contributions = rateType === 'BrokerCarrierSpot'
        ? brokerCarrierSpotContributions
        : shipperCarrierContractContributions;
    const contributionsMinMax = showsContributions
        ? getMinMaxContributionValues(rateType, contributions)
        : undefined;

    /**
     * Get the min and max values from data to show on the graph
     */
    const { min: ratesMin, max: ratesMax } = getMinMaxYValues([
        ...Object.values(brokerCarrierSpotHistorical),
        ...Object.values(shipperCarrierContractHistorical),
    ]);
    
    const minimum = min([ ratesMin, contributionsMinMax?.min ]);
    const maximum = max([ ratesMax, contributionsMinMax?.max ]);

    yAxis.setAll({
        min: minimum,
        max: maximum,
        numberFormat: perLoad ? '$#' : '$#.00',
    });
    currentGraphChartInfo.yAxis.setAll({
        min: minimum,
        max: maximum,
        numberFormat: perLoad ? '$#' : '$#.00',
    });
    currentGraphChartInfo.yAxis.labelsContainer.set('visible', false);

    // yAxis.set('step', rangeStep);
    //#endregion

    chart.appear(1);
    currentGraphChartInfo.chart.appear(1);

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}

//#endregion

//#region Historical Tooltip
export function addHistoricalTooltip(
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    legend: LegendSettings[],
    transformation: IqEventBusTransformationService,
    {
        brokerCarrierSpotHistorical,
        shipperCarrierContractHistorical,
        brokerCarrierSpotCurrent,
        shipperCarrierContractCurrent,
        perLoad,
        currentGraphChartInfo,
        currentRate,
    }: HistoricalGraphInfo,
) {
    const isBrokerCarrierSpot = currentRate === 'BrokerCarrierSpot';
    const lineId = {
        BrokerCarrierSpot: bcsHistoricalLineSeriesId,
        ShipperCarrierContract: sccHistoricalLineSeriesId,
    }[currentRate];
    chartInfo.chart.series.each((series) => {
        const id = series.get('id');
        if (id !== lineId) return;
        const showBoth =
            currentRate === 'BrokerCarrierSpot'
                ? legend.find((l) => l.id === 'historicalBrokerCarrierContract')
                      .checked
                : legend.find((l) => l.id === 'historicalBrokerCarrierSpot')
                      .checked;
        series.data.setAll(
            series.dataItems.map((item) => {
                const data = item?.dataContext as any;
                const key = data?.key;

                const spotInfo = brokerCarrierSpotHistorical[key];
                const contractInfo = shipperCarrierContractHistorical[key];
                const spotTemplate = transformation.render(
                    createSpotTooltip(),
                    {
                        title: 'Spot Rate',
                        date: DateTime.fromMillis(spotInfo.date, {
                            zone: 'UTC',
                        }).toFormat('MMMM yyyy'),
                        average: spotInfo?.average,
                        low: spotInfo?.low,
                        high: spotInfo?.high,
                        withCents: !perLoad,
                    },
                );
                const contractTemplate = transformation.render(
                    createContractTooltip(),
                    {
                        title: 'Contract Rate',
                        date: DateTime.fromMillis(spotInfo.date, {
                            zone: 'UTC',
                        }).toFormat('MMMM yyyy'),
                        average: contractInfo?.average,
                        low: contractInfo?.low,
                        high: contractInfo?.high,
                        withCents: !perLoad,
                    },
                );
                const bothTemplate = `<div class="bg-white grid grid-cols-2 gap-2 justify-center">
                ${isBrokerCarrierSpot ? spotTemplate : contractTemplate}
                ${isBrokerCarrierSpot ? contractTemplate : spotTemplate}
            </div>`;

                const template = showBoth
                    ? bothTemplate
                    : isBrokerCarrierSpot
                    ? spotTemplate
                    : contractTemplate;
                return {
                    ...(data as any),
                    template,
                };
            }),
        );

        const tooltip = series.set(
            'tooltip',
            am5.Tooltip.new(chartInfo.root, {
                getFillFromSprite: false,
                getStrokeFromSprite: false,
            }),
        );

        chartInfo.chart.plotContainer.set('tooltipPosition', 'pointer');
        chartInfo.chart.plotContainer.set('tooltipHTML', '<div></div>');
        chartInfo.chart.plotContainer.set('tooltip', tooltip);

        const borderColor = showBoth
            ? '#000'
            : isBrokerCarrierSpot
            ? GraphColors.BrokerCarrierSpot
            : GraphColors.ShipperCarrierContract;

        tooltip.get('background').setAll({
            strokeWidth: 1,
            fill: am5.color('#fff'),
            stroke: am5.color(borderColor),
            strokeOpacity: 1,
            fillOpacity: 1,
        });

        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });

        tooltip.label.adapters.add('dy', (dy) => {
            tooltip.adapters.add('bounds', function () {
                return {
                    left: 70,
                    right: 9999,
                    top: 0,
                    bottom: 20,
                };
            });
            return -tooltip._fy;
        });

        tooltip.label.adapters.add('html', function (text, _target) {
            text = '';
            chartInfo.chart.series.each(function (series) {
                if (series.get('id') !== id) return;
                var tooltipDataItem = series.get('tooltipDataItem');
                const template = tooltipDataItem?.dataContext?.['template'];
                text = template;
            });
            return text;
        });
    });
}
//#endregion
//#region Current Tooltip
export function addCurrentTooltip(
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    legend: LegendSettings[],
    transformation: IqEventBusTransformationService,
    {
        brokerCarrierSpotHistorical,
        shipperCarrierContractHistorical,
        brokerCarrierSpotCurrent,
        shipperCarrierContractCurrent,
        perLoad,
        currentGraphChartInfo,
        currentRate,
    }: HistoricalGraphInfo,
) {
    const isBrokerCarrierSpot = currentRate === 'BrokerCarrierSpot';
    const lineId = {
        BrokerCarrierSpot: bcsCurrentLineSeriesId,
        ShipperCarrierContract: sccCurrentLineSeriesId,
    }[currentRate];

    chartInfo.chart.series.each((series) => {
        const id = series.get('id');

        if (id !== lineId) return;
        series.dataItems.forEach((dataItem: { dataContext: any }) => {
            const spotInfo = brokerCarrierSpotCurrent[dataItem.dataContext.key];
            const contractInfo =
                shipperCarrierContractCurrent[dataItem.dataContext.key];

            const spotTemplate = transformation.render(createSpotTooltip(), {
                title: 'Spot Rate',
                date: spotInfo?.timeEscalation,
                average: spotInfo?.average,
                low: spotInfo?.low,
                high: spotInfo?.high,
                withCents: !perLoad,
            });
            const contractTemplate = transformation.render(
                createContractTooltip(),
                {
                    title: 'Contract Rate',
                    date: contractInfo?.timeEscalation,
                    average: contractInfo?.average,
                    low: contractInfo?.low,
                    high: contractInfo?.high,
                    withCents: !perLoad,
                },
            );
            const template = isBrokerCarrierSpot
                ? spotTemplate
                : contractTemplate;

            dataItem.dataContext = {
                ...dataItem.dataContext,
                template,
            };
        });

        const tooltip = series.set(
            'tooltip',
            am5.Tooltip.new(chartInfo.root, {
                animationDuration: 0,
                getFillFromSprite: false,
                getStrokeFromSprite: false,
                dy: 0,
            }),
        );

        chartInfo.chart.plotContainer.set('tooltipPosition', 'pointer');
        chartInfo.chart.plotContainer.set('tooltipHTML', '<div></div>');
        chartInfo.chart.plotContainer.set('tooltip', tooltip);

        const borderColor =
            currentRate === 'BrokerCarrierSpot'
                ? GraphColors.BrokerCarrierSpot
                : GraphColors.ShipperCarrierContract;

        tooltip.get('background').setAll({
            strokeWidth: 1,
            fill: am5.color('#fff'),
            stroke: am5.color(borderColor),
            strokeOpacity: 1,
            fillOpacity: 1,
        });

        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });

        tooltip.label.adapters.add('dy', (dy) => {
            tooltip.adapters.add('bounds', function () {
                return {
                    left: -80,
                    right: 9999,
                    top: 0,
                    bottom: 20,
                };
            });
            return -tooltip._fy;
        });

        tooltip.label.adapters.add('html', function (text, _target) {
            return `{template}`;
        });
    });
}
//#endregion
//#region Tooltip Templates
function createSpotTooltip() {
    return `<div class="bg-white flex flex-col justify-center">
        <label class="text-center text-dat-flame-400 text-nowrap pb-1 font-caption-strong"><%= title %></label>
        <div class="text-center text-dat-grey-900 pb-1 font-caption">
            <%= date %>
        </div>
        <div class="text-center font-caption-strong text-dat-grey-900">
            <%=  currency(average, withCents) %>
        </div>
        <div class="text-center font-caption text-dat-grey-900">
            <%=  currency(low, withCents) %>-<%=  currency(high, withCents) %>
        </div>
    </div>

`;
}
function createContractTooltip() {
    return `<div class="bg-white flex flex-col justify-center">
        <label class="text-center text-dat-blue-400 text-nowrap pb-1 font-caption-strong"><%= title %></label>
        <div class="text-center font-caption text-dat-grey-900 pb-1">
            <%= date %>
        </div>
        <div class="text-center font-caption-strong text-dat-grey-900">
             <%=  currency(average, withCents) %>
        </div>
        <div class="text-center font-caption text-dat-grey-900">
            <%=  currency(low, withCents) %>-<%=  currency(high, withCents) %>
        </div>
    </div>

`;
}

//#endregion

//#region Broker Carrier Spot Historical Series
function createBrokerCarrierSpotHistoricalSeries(
    brokerCarrierSpotHistorical,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): [am5xy.LineSeries, am5xy.CandlestickSeries] {
    const candleStick: am5xy.CandlestickSeries =
        createBrokerCarrierSpotHistoricalCandleStick(
            root,
            xAxis,
            yAxis,
            brokerCarrierSpotHistorical,
        );

    const lineSeries: am5xy.LineSeries =
        createBrokerCarrierSpotHistoricalLineSeries(
            root,
            xAxis,
            yAxis,
            brokerCarrierSpotHistorical,
        );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createBrokerCarrierSpotHistoricalLineSeries(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotHistorical: any,
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsHistoricalLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        valueXField: 'date',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.BrokerCarrierSpot);
    lineSeries.data.setAll(Object.values(brokerCarrierSpotHistorical));

    return lineSeries;
}

function createBrokerCarrierSpotHistoricalCandleStick(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotHistorical: any,
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: bcsHistoricalCandlestickSeriesId,
            name: 'HistoricalHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            valueXField: 'date',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.BrokerCarrierSpot);
    addBottomLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    addTopLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    setWidth(candleStick);

    candleStick.data.setAll(Object.values(brokerCarrierSpotHistorical));

    return candleStick;
}
//#endregion

//#region BCS Current Series
function createBrokerCarrierSpotCurrentSeries(
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotCurrent: any,
): [am5xy.LineSeries, am5xy.CandlestickSeries] {
    const candleStick: am5xy.CandlestickSeries =
        createBrokerCarrierSpotCurrentCandleStick(
            root,
            xAxis,
            yAxis,
            brokerCarrierSpotCurrent,
        );
    const lineSeries: am5xy.LineSeries = createBrokerCarrierSpotCurrentLine(
        root,
        xAxis,
        yAxis,
        brokerCarrierSpotCurrent,
    );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createBrokerCarrierSpotCurrentCandleStick(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotCurrent: any,
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: bcsCurrentCandlestickSeriesId,
            name: 'CurrentHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            categoryXField: 'timeEscalation',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.BrokerCarrierSpot);
    addBottomLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    addTopLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    setWidth(candleStick);

    xAxis.data.setAll(Object.values(brokerCarrierSpotCurrent));
    candleStick.data.setAll(Object.values(brokerCarrierSpotCurrent));

    return candleStick;
}

function createBrokerCarrierSpotCurrentLine(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotCurrent: any,
): am5xy.LineSeries {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsCurrentLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'timeEscalation',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.BrokerCarrierSpot);

    lineSeries.data.setAll(Object.values(brokerCarrierSpotCurrent));

    return lineSeries;
}
//#endregion

//#region SCC Current Series
function createShipperCarrierContractCurrentSeries(
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContract: any,
): (am5xy.LineSeries | am5xy.CandlestickSeries)[] {
    const candleStick: am5xy.CandlestickSeries =
        createShipperCarrierContractCurrentCandleStick(
            root,
            xAxis,
            yAxis,
            shipperCarrierContract,
        );
    const lineSeries: am5xy.LineSeries =
        createShipperCarrierContractCurrentLine(
            root,
            xAxis,
            yAxis,
            shipperCarrierContract,
        );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createShipperCarrierContractCurrentCandleStick(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContract: any,
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: sccCurrentCandlestickSeriesId,
            name: 'CurrentHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            categoryXField: 'timeEscalation',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.ShipperCarrierContract);
    addBottomLine(candleStick, root, GraphColors.ShipperCarrierContract);
    addTopLine(candleStick, root, GraphColors.ShipperCarrierContract);
    setWidth(candleStick);

    xAxis.data.setAll(Object.values(shipperCarrierContract));
    candleStick.data.setAll(Object.values(shipperCarrierContract));

    return candleStick;
}

function createShipperCarrierContractCurrentLine(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContract: any,
): am5xy.LineSeries {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccCurrentLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'timeEscalation',
        stroke: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.ShipperCarrierContract);

    lineSeries.data.setAll(Object.values(shipperCarrierContract));

    return lineSeries;
}
//#endregion

//#region Shipper Carrier Contract Historical Series
function createShipperCarrierContractHistoricalSeries(
    shipperCarrierContractHistorical,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): (am5xy.LineSeries | am5xy.CandlestickSeries)[] {
    const candleStick: am5xy.CandlestickSeries =
        createShipperCarrierContractHistoricalCandleStick(
            root,
            xAxis,
            yAxis,
            shipperCarrierContractHistorical,
        );

    const lineSeries: am5xy.LineSeries =
        createShipperCarrierContractHistoricalLineSeries(
            root,
            xAxis,
            yAxis,
            shipperCarrierContractHistorical,
        );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createShipperCarrierContractHistoricalLineSeries(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    ShipperCarrierContractHistorical: any,
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccHistoricalLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        valueXField: 'date',
        stroke: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.ShipperCarrierContract);

    lineSeries.data.setAll(Object.values(ShipperCarrierContractHistorical));
    return lineSeries;
}

function createShipperCarrierContractHistoricalCandleStick(
    root: am5.Root,
    xAxis: am5xy.DateAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    ShipperCarrierContractHistorical: any,
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: sccHistoricalCandlestickSeriesId,
            name: 'HistoricalHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            valueXField: 'date',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.ShipperCarrierContract);
    addBottomLine(candleStick, root, GraphColors.ShipperCarrierContract);
    addTopLine(candleStick, root, GraphColors.ShipperCarrierContract);
    setWidth(candleStick);

    candleStick.data.setAll(Object.values(ShipperCarrierContractHistorical));

    return candleStick;
}
//#endregion

//#region Line Series Utils
function addSquare(series: am5xy.XYSeries, root: am5.Root, color: string) {
    series.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: am5.Rectangle.new(root, {
                fill: am5.color(color),
                width: 8,
                height: 8,
                centerX: am5.percent(50),
                centerY: am5.percent(50),
            }),
        });
    });
}
//#endregion

//#region CandleStick Series Utils
function setWidth(series: am5xy.CandlestickSeries) {
    series.columns.template.setAll({
        width: 1,
    });
}

function setCandlestickColor(series: am5xy.CandlestickSeries, color: string) {
    series.columns.template.states.create('riseFromOpen', {
        fill: am5.color(color),
        stroke: am5.color(color),
    });
    series.columns.template.states.create('dropFromOpen', {
        fill: am5.color(color),
        stroke: am5.color(color),
    });
}

function addBottomLine(series: am5xy.XYSeries, root: am5.Root, color: string) {
    series.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: createHorizontalLine(root, color),
            locationY: 0,
        });
    });
}

function addTopLine(series: am5xy.XYSeries, root: am5.Root, color: string) {
    series.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: createHorizontalLine(root, color),
            locationY: 1,
        });
    });
}

function createHorizontalLine(root: am5.Root, color: string): am5.Rectangle {
    return am5.Rectangle.new(root, {
        fill: am5.color(color),
        width: 8,
        height: 1,
        centerX: am5.percent(50),
        centerY: am5.percent(50),
    });
}

//#endregion

//#region Utils

export function setXAxisToDaily(xAxis: am5xy.DateAxis<any>) {
    xAxis.set('gridIntervals', [{ timeUnit: 'day', count: 1 }]);
    xAxis.set('baseInterval', { timeUnit: 'day', count: 1 });
}

export function setXAxisToMonthly(xAxis: am5xy.DateAxis<any>) {
    xAxis.set('gridIntervals', [{ timeUnit: 'month', count: 1 }]);
    xAxis.set('baseInterval', { timeUnit: 'month', count: 1 });
}

function getMinMaxYValues(rates: { low: number; high: number }[] = []): {
    min: number;
    max: number;
} {
    const min = sortBy(rates, 'low')?.[0]?.low ?? 0;
    const max = sortBy(rates, 'high')?.reverse()?.[0]?.high ?? 0;

    return { min, max };
}

function getMinMaxContributionValues(rateType: string, contributions: any[] = []): { min: number, max: number } | undefined {
    if (contributions.length > 0) {
        const contributorType = contributions[0].contributorType;
        const valueKeyMap = {
            'Broker': {
                'BrokerCarrierSpot': 'totalPaidByBroker',
                'ShipperCarrierContract': 'totalPaidByShipper'
            },
            'Shipper': {
                'BrokerCarrierSpot': 'totalPaidByShipper',
                'ShipperCarrierContract': 'totalPaidByShipper'
            },
            'Carrier': {
                'BrokerCarrierSpot': 'totalPaidByBroker',
                'ShipperCarrierContract': 'totalPaidByShipper'
            }
        };

        const valueKey = valueKeyMap[contributorType][rateType];

        const sorted = sortBy(contributions, valueKey);
        
        const min = first(sorted)[valueKey];
        const max = last(sorted)[valueKey];

        return { min, max };
    }

    return undefined;
}

/**
 * Creates an array of step values from a minimum to a maximum value.
 * This is so we can add the correct label for the Y-Axis.
 *
 * @param min - minimum value
 * @param max - maximum value
 * @param step - step value
 * @returns
 */
function createSteps(min: number, max: number, step: number): number[] {
    const range = [];

    for (let i = min; i <= max; i += step) {
        range.push(i);
    }
    return range;
}

//#endregion
